/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

// Utilities for using Cognito User Pool
//
// Export functions (and used by...):
//    authenticateUser - for login (LoginDlg)
//    forgotPassword - start reset process, trigger code sending (LoginDlg)
//    confirmPassword - confirm verification code and new password (ResetDlg)
//    registerUser - start sign up, trigger code (SignUpDlg)
//    confirmRegister - confirm verification code (SignUpDlg, CodeDlgDlg)
//    resendCode - ask for resending code (CodeDlg)
//    changePassword - change password (ChgPWDlg)
//    deleteUser - delete user permanantly (ProfilePage)

import keys from "client/config/keys.ts";
import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

function getUserPool() {
  var poolData = {
    UserPoolId: keys.cognitoPoolID,
    ClientId: keys.cognitoClientID,
  };
  return new CognitoUserPool(poolData);
}

function getCognitoUser(username) {
  var userData = {
    Username: username,
    Pool: getUserPool(),
  };
  return new CognitoUser(userData);
}

// LOGIN - authenticateUser
//
async function authenticateUserAsync(user, authDetails) {
  return new Promise(function (resolve, reject, challenge) {
    user.authenticateUser(authDetails, {
      onSuccess: resolve,
      onFailure: reject,
      mfaRequired: challenge,
    });
    // }).then(result => result);
  });
}

export async function authenticateUser(username, password) {
  var authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  try {
    await authenticateUserAsync(
      getCognitoUser(username),
      authenticationDetails,
      () => true,
      () => false,
      () => false,
    );
    // success case does receive an object for further operations, but we don't need them.
    return { result: true };
  } catch (err) {
    return { result: false, err };
  }
}

// FORGOT (reset) Password
//
async function forgotPasswordAsync(user) {
  return new Promise(function (resolve, reject, challenge) {
    user.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
      inputVerificationCode: challenge,
    });
  });
}

export async function forgotPassword(username) {
  try {
    await forgotPasswordAsync(
      getCognitoUser(username),
      () => true,
      () => false,
    );
    // success case does receive some data (attribute, deliver medium, sent to...), but we don't need them.
    return { result: true };
  } catch (err) {
    return { result: false, err }; // UX can use err.code
  }
}

// CONFIRM PASSWORD, after forgot, together with Code
//
async function confirmPasswordAsync(user, code, password) {
  return new Promise(function (resolve, reject) {
    user.confirmPassword(code, password, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export async function confirmPassword(username, code, password) {
  try {
    await confirmPasswordAsync(
      getCognitoUser(username),
      code,
      password,
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { result: false, err }; // UX can use err.code
  }
}

// SIGNUP - signUp
//
async function signUpAsync(userPool, email, password, attrs) {
  return new Promise(function (resolve, reject) {
    userPool.signUp(email, password, attrs, null, function (err, result) {
      if (err) {
        reject(err); // alert(err.message || JSON.stringify(err));
      } else {
        resolve(result); // result.user is a cognitoUser. cognitoUser.getUsername() is the email entered
      }
    });
  });
}

export async function registerUser(email, password, name) {
  const attrs = [
    { Name: "email", Value: email },
    { Name: "name", Value: name },
  ];
  var attributeList = attrs.map((attr) => new CognitoUserAttribute(attr));

  try {
    await signUpAsync(
      getUserPool(),
      email,
      password,
      attributeList,
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { result: false, err };
  }
}

// CONFIRM REGISTER - confirmRegistration
//
async function confirmRegisterAsync(user, code) {
  return new Promise(function (resolve, reject) {
    user.confirmRegistration(code, true, function (err, result) {
      if (err) {
        reject(err); // alert(err.message || JSON.stringify(err));
      } else {
        resolve(result);
      }
    });
  });
}

export async function confirmRegister(email, code) {
  try {
    await confirmRegisterAsync(
      getCognitoUser(email),
      code,
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { result: false, err };
  }
}

// REQUEST NEW CODE - resendConfirmationCode
//
async function resendConfirmationCodeAsync(user) {
  return new Promise(function (resolve, reject) {
    user.resendConfirmationCode(function (err, result) {
      if (err) {
        reject(err); // alert(err.message || JSON.stringify(err));
      } else {
        resolve(result);
      }
    });
  });
}

export async function resendCode(email) {
  try {
    await resendConfirmationCodeAsync(
      getCognitoUser(email),
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { result: false, err };
  }
}

// CHANGE PASSWORD
async function changePasswordAsync(user, oldPass, newPass) {
  return new Promise(function (resolve, reject) {
    user.changePassword(oldPass, newPass, function (err, result) {
      if (err) {
        reject(err); // alert(err.message || JSON.stringify(err));
      } else {
        resolve(result);
      }
    });
  });
}

export async function changePassword(email, oldPass, newPass) {
  var authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: oldPass,
  });
  const user = getCognitoUser(email);
  try {
    // Note that we need to authenticate before request change password
    await authenticateUserAsync(
      user,
      authenticationDetails,
      () => true,
      () => false,
      () => false,
    );
    await changePasswordAsync(
      user,
      oldPass,
      newPass,
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { result: false, err };
  }
}

// DELETE USER
async function deleteUserAsync(user) {
  return new Promise(function (resolve, reject) {
    user.deleteUser(function (err, result) {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
}

export async function deleteAWSUser(email, password) {
  var authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });
  const user = getCognitoUser(email);
  try {
    // Note that we need to authenticate before request delete user
    await authenticateUserAsync(
      user,
      authenticationDetails,
      () => true,
      () => false,
      () => false,
    );
    await deleteUserAsync(
      user,
      () => true,
      () => false,
    );
    return { result: true };
  } catch (err) {
    return { return: false, err };
  }
}
